// src/pages/Retention/Retention.jsx
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Stack,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Sms as SmsIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import {
  useGetRetentionStatsQuery,
  useGetRetentionUsersQuery,
  useGetCampaignStatsQuery,
  useGetRiskTransitionStatsQuery,
  useGetEngagementStatsQuery,
  useGetCohortBreakdownsQuery,
} from '../../store/api/retentionApi';
import CohortBreakdowns from './CohortBreakdowns';

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', trend }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color: `${color}.main` }} />
        </Box>
      </Stack>
      {trend !== undefined && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
          {trend >= 0 ? (
            <TrendingUpIcon fontSize="small" color="success" />
          ) : (
            <TrendingDownIcon fontSize="small" color="error" />
          )}
          <Typography variant="caption" color={trend >= 0 ? 'success.main' : 'error.main'}>
            {Math.abs(trend)}%
          </Typography>
        </Stack>
      )}
    </CardContent>
  </Card>
);

// Churn Risk Chip
const ChurnRiskChip = ({ risk }) => {
  const colors = {
    Low: 'success',
    Medium: 'warning',
    High: 'error',
  };
  return <Chip label={risk} size="small" color={colors[risk] || 'default'} />;
};

// Engagement Tier Chip
const EngagementTierChip = ({ tier }) => {
  const colors = {
    VIP: 'success',
    Loyal: 'success',
    Regular: 'info',
    Casual: 'default',
    'At-Risk': 'warning',
    Dormant: 'error',
  };
  return <Chip label={tier} size="small" color={colors[tier] || 'default'} />;
};

// Engagement Trend Indicator
const EngagementTrendIcon = ({ trend }) => {
  if (trend === 'improving') {
    return <TrendingUpIcon fontSize="small" color="success" />;
  } else if (trend === 'declining') {
    return <TrendingDownIcon fontSize="small" color="error" />;
  }
  return <ScheduleIcon fontSize="small" color="disabled" />;
};

// Engagement Score Display - Circular progress with score
const EngagementScoreDisplay = ({ score }) => {
  const value = score || 0;
  const color = value >= 70 ? 'success' : value >= 40 ? 'warning' : 'error';
  
  return (
    <Tooltip title={`Engagement Score: ${value}/100`}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={value}
          size={40}
          thickness={4}
          color={color}
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: '50%',
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="caption"
            component="div"
            fontWeight="bold"
            color={`${color}.main`}
            sx={{ fontSize: '0.7rem' }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

// Custom Toolbar
const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarFilterButton />
    <GridToolbarExport />
  </GridToolbarContainer>
);

const Retention = () => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [filters, setFilters] = useState({ churnRisk: '', search: '', hasBonus: '', engagementTier: '' });

  // API Queries
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useGetRetentionStatsQuery();
  const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useGetRetentionUsersQuery({
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
    ...filters,
  });
  const { data: campaignData } = useGetCampaignStatsQuery();
  const { data: riskTransitionData } = useGetRiskTransitionStatsQuery();
  const { data: engagementData } = useGetEngagementStatsQuery();
  const { data: cohortBreakdownData, isLoading: cohortBreakdownsLoading, refetch: refetchCohortBreakdowns } = useGetCohortBreakdownsQuery();

  const stats = statsData?.data || {};
  const users = usersData?.data?.users || [];
  const pagination = usersData?.data?.pagination || { total: 0 };
  const campaigns = campaignData?.data || [];
  const riskTransitions = riskTransitionData?.data || {};
  const engagement = engagementData?.data || {};
  const cohortBreakdowns = cohortBreakdownData?.data || {};

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleRefresh = () => {
    refetchStats();
    refetchUsers();
    refetchCohortBreakdowns();
  };

  // Quick health thresholds for at-a-glance RAG coloring.
  const getStickinessColor = (value) => {
    if (value >= 30) return 'success';
    if (value >= 20) return 'warning';
    return 'error';
  };

  const getMonthlyStickinessColor = (value) => {
    if (value >= 15) return 'success';
    if (value >= 10) return 'warning';
    return 'error';
  };

  const getRiskPressureColor = (value) => {
    if (value <= 20) return 'success';
    if (value <= 35) return 'warning';
    return 'error';
  };

  const getCostEfficiencyColor = (value) => {
    if (value <= 5) return 'success';
    if (value <= 12) return 'warning';
    return 'error';
  };

  const getResponseRateColor = (value) => {
    if (value >= 20) return 'success';
    if (value >= 10) return 'warning';
    return 'error';
  };

  const getRoiColor = (value) => {
    if (value >= 20) return 'success';
    if (value >= 0) return 'warning';
    return 'error';
  };

  const getCohortColor = (value, day) => {
    if (day === 1) return value >= 25 ? 'success' : value >= 15 ? 'warning' : 'error';
    if (day === 7) return value >= 15 ? 'success' : value >= 8 ? 'warning' : 'error';
    if (day === 30) return value >= 5 ? 'success' : value >= 2 ? 'warning' : 'error';
    return 'info';
  };

  // DataGrid columns
  const columns = [
    {
      field: 'phoneNumber',
      headerName: 'Phone',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'churnRisk',
      headerName: 'Churn Risk',
      width: 110,
      renderCell: (params) => <ChurnRiskChip risk={params.value} />,
    },
    {
      field: 'engagementTier',
      headerName: 'Engagement',
      width: 100,
      renderCell: (params) => <EngagementTierChip tier={params.value} />,
    },
    {
      field: 'engagementScore',
      headerName: 'Score',
      width: 80,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <EngagementScoreDisplay score={params.value} />,
    },
    {
      field: 'engagementTrend',
      headerName: 'Trend',
      width: 70,
      renderCell: (params) => <EngagementTrendIcon trend={params.value} />,
    },
    {
      field: 'totalGamesPlayed',
      headerName: 'Games',
      width: 80,
      type: 'number',
    },
    {
      field: 'totalStake',
      headerName: 'Total Stake',
      width: 110,
      type: 'number',
      valueFormatter: (value) => `K${(value || 0).toFixed(2)}`,
    },
    {
      field: 'lifetimeValue',
      headerName: 'LTV',
      width: 100,
      type: 'number',
      valueFormatter: (value) => `K${(value || 0).toFixed(2)}`,
      cellClassName: (params) => (params.value >= 0 ? 'positive' : 'negative'),
    },
    {
      field: 'winRate',
      headerName: 'Win Rate',
      width: 90,
      type: 'number',
      valueFormatter: (value) => `${((value || 0) * 100).toFixed(0)}%`,
    },
    {
      field: 'sessionCount',
      headerName: 'Sessions',
      width: 80,
      type: 'number',
    },
    {
      field: 'averageSessionFrequency',
      headerName: 'Avg Freq (days)',
      width: 120,
      type: 'number',
      valueFormatter: (value) => (value || 0).toFixed(1),
    },
    {
      field: 'lastPlayedAt',
      headerName: 'Last Played',
      width: 120,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      valueFormatter: (value) => value ? value.toLocaleDateString() : '-',
    },
    {
      field: 'pendingBonusPoints',
      headerName: 'Bonus Pts',
      width: 90,
      type: 'number',
      renderCell: (params) =>
        params.value > 0 ? (
          <Chip label={params.value} size="small" color="info" />
        ) : (
          '-'
        ),
    },
    {
      field: 'lastRetentionCampaign',
      headerName: 'Last Campaign',
      width: 120,
      renderCell: (params) =>
        params.value ? <Chip label={params.value} size="small" variant="outlined" /> : '-',
    },
    {
      field: 'respondedToLastMessage',
      headerName: 'Responded',
      width: 100,
      renderCell: (params) =>
        params.row.lastRetentionSentAt ? (
          params.value ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <ScheduleIcon color="warning" fontSize="small" />
          )
        ) : (
          '-'
        ),
    },
    {
      field: 'smsNoResponseCount',
      headerName: 'Ignored',
      width: 80,
      type: 'number',
      renderCell: (params) =>
        params.value > 5 ? (
          <Chip label={params.value} size="small" color="error" />
        ) : (
          params.value || 0
        ),
    },
  ];

  const rows = users.map((user) => ({ id: user._id, ...user }));

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Retention Monitoring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track player behavior and retention campaign performance
          </Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Stats Loading */}
      {statsLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers?.toLocaleString() || 0}
            subtitle={`${stats.activeToday || 0} active today`}
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active This Week"
            value={stats.activeWeek?.toLocaleString() || 0}
            subtitle={`${(stats.totalUsers > 0 ? ((stats.activeWeek / stats.totalUsers) * 100).toFixed(1) : '0.0')}% of total`}
            icon={TrendingUpIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Messages Sent"
            value={stats.messages?.totalSent?.toLocaleString() || 0}
            subtitle={`${stats.messages?.responseRate || 0}% response rate`}
            icon={SmsIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Retention Spend"
            value={`K${stats.messages?.totalSpend || 0}`}
            subtitle={`${stats.messages?.avgROI || 0}% avg ROI`}
            icon={MoneyIcon}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Active Today Split */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6}>
          <StatCard
            title="Completely New Today"
            value={stats.activeTodayNewCustomers?.toLocaleString() || 0}
            subtitle={`Out of ${stats.activeToday || 0} active customers today`}
            icon={PersonAddIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard
            title="Existing Today"
            value={stats.activeTodayExistingCustomers?.toLocaleString() || 0}
            subtitle={`Out of ${stats.activeToday || 0} active customers today`}
            icon={GroupIcon}
            color="primary"
          />
        </Grid>
      </Grid>

      {/* Rolling Cohort Retention */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="D1+ Rolling Retention"
            value={`${stats.cohortRetention?.d1 || 0}%`}
            subtitle="Returned anytime 1+ days after first play"
            icon={TrendingUpIcon}
            color={getCohortColor(stats.cohortRetention?.d1 || 0, 1)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="D7+ Rolling Retention"
            value={`${stats.cohortRetention?.d7 || 0}%`}
            subtitle="Returned anytime 7+ days after first play"
            icon={TrendingUpIcon}
            color={getCohortColor(stats.cohortRetention?.d7 || 0, 7)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="D30+ Rolling Retention"
            value={`${stats.cohortRetention?.d30 || 0}%`}
            subtitle="Returned anytime 30+ days after first play"
            icon={TrendingUpIcon}
            color={getCohortColor(stats.cohortRetention?.d30 || 0, 30)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Never Returned After Day 0"
            value={`${stats.cohortRetention?.oneAndDonePct || 0}%`}
            subtitle={`${(stats.cohortRetention?.oneAndDoneUsers || 0).toLocaleString()} of ${(stats.cohortRetention?.oneAndDoneEligible || 0).toLocaleString()} players who first played 1+ days ago`}
            icon={WarningIcon}
            color={(stats.cohortRetention?.oneAndDonePct || 0) >= 70 ? 'error' : (stats.cohortRetention?.oneAndDonePct || 0) >= 50 ? 'warning' : 'success'}
          />
        </Grid>
      </Grid>

      <CohortBreakdowns data={cohortBreakdowns} isLoading={cohortBreakdownsLoading} />

      {/* Business Health Snapshot */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Stickiness (DAU/WAU)"
            value={`${stats.businessHealth?.dauWau || 0}%`}
            subtitle="Daily active share of weekly active users"
            icon={TrendingUpIcon}
            color={getStickinessColor(stats.businessHealth?.dauWau || 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Stickiness (DAU/MAU)"
            value={`${stats.businessHealth?.dauMau || 0}%`}
            subtitle={`${stats.activeMonth || 0} active in last 30 days`}
            icon={PeopleIcon}
            color={getMonthlyStickinessColor(stats.businessHealth?.dauMau || 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Risk Pressure"
            value={`${stats.businessHealth?.riskPressure || 0}%`}
            subtitle="Users currently in Medium + High churn risk"
            icon={WarningIcon}
            color={getRiskPressureColor(stats.businessHealth?.riskPressure || 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cost / Reactivated User"
            value={`K${stats.businessHealth?.costPerReactivatedUser || 0}`}
            subtitle={`${stats.businessHealth?.totalReactivations || 0} total reactivations`}
            icon={MoneyIcon}
            color={getCostEfficiencyColor(stats.businessHealth?.costPerReactivatedUser || 0)}
          />
        </Grid>
      </Grid>

      {/* Recent Retention Efficiency */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="7D Response Rate"
            value={`${stats.windows?.sevenDays?.responseRate || 0}%`}
            subtitle={`${stats.windows?.sevenDays?.respondedUsers || 0}/${stats.windows?.sevenDays?.touchedUsers || 0} users responded`}
            icon={SmsIcon}
            color={getResponseRateColor(stats.windows?.sevenDays?.responseRate || 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="7D Cost / Response"
            value={`K${stats.windows?.sevenDays?.costPerResponseWithFees || 0}`}
            subtitle={`K${stats.windows?.sevenDays?.totalCostWithFees || 0} total cost (incl. fees)`}
            icon={MoneyIcon}
            color={getCostEfficiencyColor(stats.windows?.sevenDays?.costPerResponseWithFees || 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="30D Response Rate"
            value={`${stats.windows?.thirtyDays?.responseRate || 0}%`}
            subtitle={`${stats.windows?.thirtyDays?.respondedUsers || 0}/${stats.windows?.thirtyDays?.touchedUsers || 0} users responded`}
            icon={CheckCircleIcon}
            color={getResponseRateColor(stats.windows?.thirtyDays?.responseRate || 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="30D Avg ROI"
            value={`${stats.windows?.thirtyDays?.avgROI || 0}%`}
            subtitle={`Revenue K${stats.windows?.thirtyDays?.totalRevenue || 0} vs cost K${stats.windows?.thirtyDays?.totalCost || 0}`}
            icon={TrendingUpIcon}
            color={getRoiColor(stats.windows?.thirtyDays?.avgROI || 0)}
          />
        </Grid>
      </Grid>

      {/* Net Retention Profit */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Net Retention Profit (7D)"
            value={`K${stats.netProfit?.sevenDays || 0}`}
            subtitle="Revenue - (SMS + bonus + AI + fees)"
            icon={MoneyIcon}
            color={(stats.netProfit?.sevenDays || 0) >= 0 ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Net Retention Profit (30D)"
            value={`K${stats.netProfit?.thirtyDays || 0}`}
            subtitle="Revenue - (SMS + bonus + AI + fees)"
            icon={MoneyIcon}
            color={(stats.netProfit?.thirtyDays || 0) >= 0 ? 'success' : 'error'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="ARPU (30D)"
            value={`K${stats.arpu?.arpu30d || 0}`}
            subtitle={`${stats.arpu?.activeUsers30d || 0} active users • K${stats.arpu?.revenue30d || 0} total`}
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          30D Retention Cost Breakdown
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">SMS Cost</Typography>
            <Typography variant="h6" fontWeight="bold">K{stats.windows?.thirtyDays?.totalSmsCost || 0}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Bonus Cost</Typography>
            <Typography variant="h6" fontWeight="bold">K{stats.windows?.thirtyDays?.totalBonusCost || 0}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">AI Cost</Typography>
            <Typography variant="h6" fontWeight="bold">K{stats.windows?.thirtyDays?.totalAiCost || 0}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Payment Fees</Typography>
            <Typography variant="h6" fontWeight="bold">K{stats.windows?.thirtyDays?.totalPaymentFee || 0}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Revenue Quality by Risk Segment */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Revenue by Risk Segment
            </Typography>
            <Stack spacing={1.2}>
              {['Low', 'Medium', 'High'].map((risk) => (
                <Box key={risk}>
                  <Typography variant="body2" color="text.secondary">
                    {risk} Risk
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    K{stats.segmentRevenue?.[risk]?.totalStake || 0} ({stats.segmentShares?.[risk] || 0}%)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Users: {stats.segmentRevenue?.[risk]?.users || 0} • Avg LTV: K{stats.segmentRevenue?.[risk]?.avgLtv || 0}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Deposit After Campaign
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Deposits / Reactivated User
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="info.main">
                {stats.depositMetrics?.depositsPerReactivatedUser || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Deposit After Campaign
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                K{stats.depositMetrics?.avgDepositAfterCampaign || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stats.depositMetrics?.usersWithDeposit72 || 0}/{stats.depositMetrics?.reactivatedUsers || 0} reactivated users deposited (72h)
              </Typography>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Deposit Split (Post-Campaign)
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Deposits within 24h
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {stats.depositMetrics?.depositSplit?.within24hCount || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Deposits in 24h-72h
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {stats.depositMetrics?.depositSplit?.between24hAnd72hCount || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total 72h deposits: {stats.depositMetrics?.depositSplit?.within72hCount || 0}
              </Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Velocity + Movement */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Sessions / Active User"
            value={stats.velocity?.avgSessionsPerActiveUser || 0}
            subtitle="Average session count among active users"
            icon={TrendingUpIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Days Between Sessions"
            value={stats.velocity?.avgDaysBetweenSessions || 0}
            subtitle="Lower is generally better for retention"
            icon={ScheduleIcon}
            color={(stats.velocity?.avgDaysBetweenSessions || 0) <= 3 ? 'success' : (stats.velocity?.avgDaysBetweenSessions || 0) <= 7 ? 'warning' : 'error'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="% Play Within 24h of Deposit"
            value={`${stats.velocity?.usersPlayingWithin24hOfDepositPct || 0}%`}
            subtitle="Depositors who placed at least one bet in 24h"
            icon={CheckCircleIcon}
            color={getResponseRateColor(stats.velocity?.usersPlayingWithin24hOfDepositPct || 0)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Low → High (Approx 7D)"
            value={stats.tierMovementApprox7d?.lowToHigh || 0}
            subtitle="Escalations in latest 7-day snapshot"
            icon={WarningIcon}
            color={(stats.tierMovementApprox7d?.lowToHigh || 0) > (stats.tierMovementApprox7d?.highToMediumOrLow || 0) ? 'error' : 'success'}
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Risk Movement Snapshot (Approx 7D)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">High → Medium/Low</Typography>
            <Typography variant="h6" fontWeight="bold" color="success.main">
              {stats.tierMovementApprox7d?.highToMediumOrLow || 0}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Medium → Low</Typography>
            <Typography variant="h6" fontWeight="bold" color="success.main">
              {stats.tierMovementApprox7d?.mediumToLow || 0}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Low → Medium</Typography>
            <Typography variant="h6" fontWeight="bold" color="warning.main">
              {stats.tierMovementApprox7d?.lowToMedium || 0}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">Low → High</Typography>
            <Typography variant="h6" fontWeight="bold" color="error.main">
              {stats.tierMovementApprox7d?.lowToHigh || 0}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Churn Risk Breakdown */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Churn Risk Breakdown
            </Typography>
            <Stack spacing={2}>
              {['Low', 'Medium', 'High'].map((risk) => {
                const count = stats.churnRisk?.[risk] || 0;
                const total = stats.totalUsers || 1;
                const percent = ((count / total) * 100).toFixed(1);
                const color = risk === 'Low' ? 'success' : risk === 'Medium' ? 'warning' : 'error';
                return (
                  <Box key={risk}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{risk} Risk</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {count} ({percent}%)
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(percent)}
                      color={color}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Pending Bonuses
            </Typography>
            <Stack spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Users with pending bonus
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {stats.pendingBonus?.users || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total bonus points pending
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {(stats.pendingBonus?.totalPoints || 0).toLocaleString()}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Platform Metrics
            </Typography>
            <Stack spacing={1}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Lifetime Value
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  K{stats.metrics?.avgLifetimeValue || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Win Rate
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {stats.metrics?.avgWinRate || 0}%
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Engagement Score
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {stats.businessHealth?.avgEngagementScore || 0}/100
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  VIP + Loyal Stake Share
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {stats.businessHealth?.vipLoyalStakeShare || 0}%
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Campaign Performance */}
      {campaigns.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Campaign Performance
          </Typography>
          <Grid container spacing={2}>
            {campaigns.map((c) => (
              <Grid item xs={12} sm={6} md={3} key={c.campaign}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">
                      {c.campaign}
                    </Typography>
                    <Typography variant="h6">{c.totalSent} sent</Typography>
                    <Stack direction="row" spacing={1} mt={1}>
                      <Chip
                        label={`${c.responseRate?.toFixed(0)}% response`}
                        size="small"
                        color={c.responseRate > 10 ? 'success' : 'default'}
                      />
                      <Chip
                        label={`${c.avgROI}% ROI`}
                        size="small"
                        color={c.avgROI > 0 ? 'success' : 'error'}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Engagement & Risk Transition Analytics */}
      <Grid container spacing={3} mb={3}>
        {/* Engagement Tier Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Engagement Tiers
            </Typography>
            <Stack spacing={2}>
              {['VIP', 'Loyal', 'Regular', 'Casual', 'At-Risk', 'Dormant'].map((tier) => {
                const count = engagement.tierBreakdown?.[tier] || 0;
                const total = stats.totalUsers || 1;
                const percent = ((count / total) * 100).toFixed(1);
                const color = ['VIP', 'Loyal'].includes(tier) ? 'success' :
                  tier === 'Regular' ? 'info' :
                  tier === 'Casual' ? 'primary' :
                  tier === 'At-Risk' ? 'warning' : 'error';
                return (
                  <Box key={tier}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{tier}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {count} ({percent}%)
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(parseFloat(percent), 100)}
                      color={color}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        {/* Risk Transition Stats */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Re-engagement Analytics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Re-engaged from Medium Risk
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="warning.main">
                      {riskTransitions.mediumRisk?.usersReEngaged || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg {riskTransitions.mediumRisk?.avgDaysToReEngage || 0} days to return
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Re-engaged from High Risk
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {riskTransitions.highRisk?.usersReEngaged || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg {riskTransitions.highRisk?.avgDaysToReEngage || 0} days to return
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Churned from Medium Risk
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {riskTransitions.mediumRisk?.churnedCount || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {riskTransitions.mediumRisk?.reEngagementRate || 0}% re-engagement rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      Churned from High Risk
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {riskTransitions.highRisk?.churnedCount || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {riskTransitions.highRisk?.reEngagementRate || 0}% re-engagement rate
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search phone..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Churn Risk</InputLabel>
            <Select
              value={filters.churnRisk}
              label="Churn Risk"
              onChange={(e) => handleFilterChange('churnRisk', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="High">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Has Bonus</InputLabel>
            <Select
              value={filters.hasBonus}
              label="Has Bonus"
              onChange={(e) => handleFilterChange('hasBonus', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">With Bonus</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Engagement</InputLabel>
            <Select
              value={filters.engagementTier}
              label="Engagement"
              onChange={(e) => handleFilterChange('engagementTier', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="VIP">VIP</MenuItem>
              <MenuItem value="Loyal">Loyal</MenuItem>
              <MenuItem value="Regular">Regular</MenuItem>
              <MenuItem value="Casual">Casual</MenuItem>
              <MenuItem value="At-Risk">At-Risk</MenuItem>
              <MenuItem value="Dormant">Dormant</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Users Table */}
      <Paper sx={{ height: 600 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={usersLoading}
          rowCount={pagination.total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          slots={{ toolbar: CustomToolbar }}
          sx={{
            '& .positive': { color: 'success.main' },
            '& .negative': { color: 'error.main' },
          }}
        />
      </Paper>
    </Box>
  );
};

export default Retention;
